import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Drawer,
  Grid2 as Grid,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import axios from "axios";
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Magnifier from "../customComponents/Magnifier";
import SelectPermitPackageForms from "../buildingComponents/SelectPermitPackageForms";
import { useDispatch, useSelector } from "react-redux";
import {
  addAvailablePermitForms,
  setNewTempPermitData,
  setQuickActionAddress,
} from "../../store/actions/dataActions";
import {
  setSelectedPermitType as setSelectedPermitTypeAction,
  setAttachingNewPermit,
} from "../../store/actions/uiActions/myPermitsActions";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { LoadingButton } from "@mui/lab";
import { setExpiredSession } from "../../store/actions/sessionsActions";
import { disconnectWebsocket } from "../../store/actions/websocketActions";
import { tourSteps } from "../../data/walkthroughDemo/steps";
import { useTour } from "@reactour/tour";
import { permitMapping } from "../../data/permitFormsData";
import { isSignedUrlExpired } from "../../functions/helperFunctions";
//

const InfoChip = ({ label, value }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 1,
      px: 1.2,
      py: 0.6,
      borderRadius: 2,
      background: "linear-gradient(180deg, #ffffff, #f4f8ff)",
      border: "1px solid #dbe7fb",
      boxShadow:
        "0 1px 1px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.8)",
    }}
  >
    <Typography variant="caption" sx={{ fontWeight: 700, color: "#3366aa" }}>
      {label}:
    </Typography>
    <Typography variant="caption" sx={{ color: "#234" }}>
      {value}
    </Typography>
  </Box>
);

const StartPermit = (props) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession].user);
  const { quickActions } = useSelector((state) => state.nocData);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { fromPermit, setStartPermitOpen, setDisplayImage } = props;

  const leftGridRef = useRef(null);
  const isFetchingRef = useRef(false);
  const prevImagesRef = useRef(null);
  const quickAddressRef = useRef(null);

  const [selectedCity, setSelectedCity] = useState("");
  const [selectedPermitType, setSelectedPermitType] = useState("");
  const attachingNewPermit = useSelector(
    (state) => state.myPermitsComponent?.attachingNewPermit
  );
  const [availableForms, setAvailableForms] = useState([]);
  const [selectedForms, setSelectedForms] = useState([]);
  const [selectedCountyForms, setSelectedCountyForms] = useState([]);
  const [stormwaterSelection, setStormwaterSelection] = useState(null);
  const [privateProviderSignerType, setPrivateProviderSignerType] =
    useState(null);

  const [countyLoading, setCountyLoading] = useState(true);
  const [cities, setCities] = useState([]);
  const [groupedForms, setGroupedForms] = useState([]);
  const [page, setPage] = useState(0);
  const [contractorIndex, setContractorIndex] = useState(0);
  const [startPermitLoading, setStartPermitLoading] = useState(false);
  const [ppName, setPpName] = useState("");

  const [contractors, setContractors] = useState([]);
  const [isPP, setIsPP] = useState(false);
  const [formContractors, setFormContractors] = useState({});

  // Email configuration for Generator Permit in St. Johns
  const [autoEmailEnabled, setAutoEmailEnabled] = useState(false);
  const [cityEmail, setCityEmail] = useState("");
  const [responseEmail, setResponseEmail] = useState("");
  const [emailRows, setEmailRows] = useState(null); // Initialize as null to allow GeneratorPermitEmailStep to prefill user email

  const { isOpen, currentStep, setCurrentStep } = useTour();

  const up1177 = useMediaQuery("(min-width:1177px)");

  const [connectToCity, setConnectToCity] = useState(false);
  const [selectedCityUser, setSelectedCityUser] = useState(null);
  const [addNewUser, setAddNewUser] = useState(false);
  const [signedLoading, setSignedLoading] = useState(false);

  const [emailRowOnError, setEmailRowOnError] = useState([]); // Array of {rowIndex, field} objects

  // Validation: Check if contractors are required and selected for Generator Permit
  const areContractorsRequired = () => {
    // Only check for Generator Permit in St. Johns County on page 0
    if (page !== 0 || selectedPermitType !== "generator_permit") return false;

    const countyLower = props?.county?.toLowerCase();
    const isStJohns =
      countyLower === "st_johns" ||
      countyLower === "st johns" ||
      countyLower === "st. johns";

    return isStJohns;
  };

  const areRequiredContractorsSelected = () => {
    if (!areContractorsRequired()) return true; // Not required, so validation passes

    // Forms that require contractors for Generator Permit
    const formsRequiringContractors = [
      "electrical_permit",
      "gas_permit",
      "noc",
    ];

    // Check if any selected form requires a contractor but doesn't have one
    for (const formName of selectedForms) {
      if (formsRequiringContractors.includes(formName)) {
        if (!formContractors[formName]) {
          return false; // Required form is selected but no contractor assigned
        }
      }
    }

    return true; // All required contractors are selected
  };

  // Validation for email configuration on page 1 for Generator Permit
  const isEmailConfigurationValid = () => {
    // Only validate on page 1 for Generator Permit in St. Johns
    if (page !== 1 || selectedPermitType !== "generator_permit") return true;

    const countyLower = props?.county?.toLowerCase();
    const isStJohns =
      countyLower === "st_johns" ||
      countyLower === "st johns" ||
      countyLower === "st. johns";

    if (!isStJohns) return true; // Not St. Johns, validation passes

    // If auto-email is disabled, validation passes
    if (!autoEmailEnabled) return true;

    // If auto-email is enabled, validate emailRows
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Check if at least one row has valid emails
    const hasValidRow =
      emailRows &&
      emailRows.length > 0 &&
      emailRows.some(
        (row) =>
          (row.cc && emailRegex.test(row.cc)) ||
          (row.bcc && emailRegex.test(row.bcc))
      );

    // Also support old format for backward compatibility
    const hasOldFormatEmails =
      (cityEmail && emailRegex.test(cityEmail)) ||
      (responseEmail && emailRegex.test(responseEmail));

    return hasValidRow || hasOldFormatEmails;
  };

  const handleImageError = async (index) => {
    try {
      setSignedLoading(true);

      // For additional permit mode, call the backend to refresh signed URLs
      if (attachingNewPermit?.started && attachingNewPermit?.taskId) {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/building/update-signed-pp-url`,
          {
            task_id: attachingNewPermit.taskId,
            currentSession: currentSession,
            currentForm: "noc", // For NOC images
            isAdditionalPermit: true,
            selectedPermitType: selectedPermitType,
            mainPermitType:
              selectedPermitType === attachingNewPermit?.permitType,
          },
          { withCredentials: true }
        );

        if (
          response?.data?.success &&
          Object.keys(response?.data?.data || []).length > 0
        ) {
          const updatedPackage = response?.data?.data;
          const nocDoc = updatedPackage?.documents?.noc;
          const nocImages = nocDoc?.signed || [];

          if (nocImages.length > index) {
            setSignedLoading(false);
            return nocImages[index];
          }
        }
      }

      setSignedLoading(false);
      return null;
    } catch (error) {
      setSignedLoading(false);
      console.error("Error handling image error:", error);
      return null;
    }
  };

  useEffect(() => {
    const provider = currentSession === "privateProviderSession";
    let contractorList = [];

    if (provider) {
      contractorList = currentUser?.managed_contractors?.data || [];
      setIsPP(true);
    } else {
      contractorList = currentUser?.user?.contractors || [];
      setIsPP(false);
    }

    // get valid prefixes for the selected permit
    const allowedPrefixes = permitMapping[selectedPermitType] || [];

    // filter contractors based on licenseNumber
    contractorList = contractorList.filter((c) =>
      allowedPrefixes.some((prefix) => c.licenseNumber?.startsWith(prefix))
    );

    // If attaching additional permit, exclude contractors already used in the package
    if (
      attachingNewPermit?.started &&
      Array.isArray(attachingNewPermit?.usedContractors)
    ) {
      const usedIds = new Set(
        attachingNewPermit.usedContractors
          .map((c) => c?.licenseNumber || c?.id)
          .filter(Boolean)
      );
      contractorList = contractorList.filter(
        (c) => !usedIds.has(c?.licenseNumber || c?.id)
      );
    }

    setContractors(contractorList);
  }, [
    currentSession,
    currentUser?.managed_contractors?.data,
    currentUser?.user?.contractors,
    selectedPermitType,
    attachingNewPermit?.started,
    attachingNewPermit?.usedContractors,
  ]);

  // // When attaching additional permit, display existing NOC signed image and a banner on page 0
  // useEffect(() => {
  //   if (!props?.fromPermit || Object.keys(props.fromPermit).length === 0) {
  //     if (attachingNewPermit?.started && page === 0) {
  //       const pkg = attachingNewPermit?.packageSnapshot;
  //       const nocDoc = pkg?.documents?.noc;
  //       const nocImgs = nocDoc?.signed || [];

  //       if (Array.isArray(nocImgs) && nocImgs.length > 0) {
  //         setDisplayImage(nocImgs);
  //       } else {
  //         // If NOC signed array is empty, try to refresh it
  //         if (attachingNewPermit?.taskId && nocDoc) {
  //           handleRefreshNocSignedUrls();
  //         } else {
  //           // Set empty array to prevent loading spinner
  //           setDisplayImage([]);
  //         }
  //       }
  //     }
  //   }
  // }, [
  //   attachingNewPermit?.started,
  //   attachingNewPermit?.packageSnapshot,
  //   attachingNewPermit?.taskId,
  //   page,
  //   props?.fromPermit,
  //   setDisplayImage,
  //   handleRefreshNocSignedUrls,
  // ]);

  useEffect(() => {
    const quickCityRaw = quickActions?.address?.city;

    if (!quickCityRaw || quickAddressRef?.current || !cities?.length) {
      return;
    }

    const sanitize = (value) => {
      if (value === undefined || value === null) {
        return "";
      }
      const [firstPart] = String(value).split(",");
      return firstPart?.trim() || "";
    };

    const normalize = (value) =>
      sanitize(value)
        .toLowerCase()
        .replace(/\(.*?\)/g, " ")
        .replace(/\b(city|town|village)\s+of\b/g, " ")
        .replace(/[^a-z0-9]+/g, " ")
        .replace(
          /\b(city|county|town|village|municipality|state|fl|florida|of|the)\b/g,
          ""
        )
        .replace(/\s+/g, " ")
        .trim();

    const quickCityNormalized = normalize(quickCityRaw);

    if (!quickCityNormalized) {
      return;
    }

    const matchedCity = cities?.find((cityName) => {
      const normalizedCity = normalize(cityName);

      if (!normalizedCity) return false;
      if (normalizedCity === quickCityNormalized) return true;

      return (
        normalizedCity.startsWith(quickCityNormalized) ||
        quickCityNormalized.startsWith(normalizedCity)
      );
    });

    if (matchedCity) {
      setSelectedCity(matchedCity);
      dispatch(
        setQuickActionAddress({
          county: quickActions?.address?.county,
          city: "",
        })
      );
      quickAddressRef.current = true;
    }
  }, [
    quickActions?.address?.city,
    quickActions?.address?.county,
    dispatch,
    cities,
  ]);

  useEffect(() => {
    if (isOpen) {
      if (currentSession === "userSession") {
        if (currentStep === 11 && page === 0) {
          setPage(1);
        } else if (currentStep === 10 && page === 1) {
          setPage(0);
        }
      } else if (currentSession === "subUserSession") {
        if (currentStep === 9 && page === 0) {
          setPage(1);
        } else if (currentStep === 8 && page === 1) {
          setPage(0);
        }
      }
    }
  }, [currentStep, currentSession, isOpen, page]);

  useEffect(() => {
    if (isOpen) {
      const num = currentSession === "userSession" ? 9 : 7;
      if (!countyLoading && currentStep === num) {
        setCurrentStep((s) => (s === tourSteps?.length - 1 ? 0 : s + 1));
      }
    }
  }, [currentSession, currentStep, isOpen, setCurrentStep, countyLoading]);

  //! //////////////////////////////////////////////////////////////

  useEffect(() => {
    if (props?.fromPermit && Object.keys(props.fromPermit).length > 0) {
      const tempCity =
        props.fromPermit.selectedCity?.toLowerCase().replace(/\s+/g, "_") || "";
      setSelectedCity(tempCity);
      setAvailableForms(props?.fromPermit?.availableForms);
      setSelectedForms(props?.fromPermit?.selectedForms);
      setCities(props?.fromPermit?.cities);
      setPage(1);
      setCountyLoading(false);
    }
  }, [props?.fromPermit]);

  // Handle city selection for additional permits
  useEffect(() => {
    if (attachingNewPermit?.started && attachingNewPermit?.city) {
      // Convert city to title case to match the dropdown options
      const formattedCity = attachingNewPermit.city
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
      setSelectedCity(formattedCity);
    }
  }, [attachingNewPermit?.started, attachingNewPermit?.city]);

  const handleCloseStartPermit = useCallback(() => {
    if (isOpen) return;

    setPpName("");
    setPage(0);
    setConnectToCity(false);
    setSelectedCity("");
    setSelectedCityUser(null);
    setAddNewUser(false);
    setAvailableForms([]);
    setStartPermitOpen(false);
    setDisplayImage([]);
    setSelectedCountyForms([]);
    setSelectedForms([]);
    setSelectedPermitType("");

    // Clear the attaching new permit state when closing the modal
    if (attachingNewPermit?.started) {
      dispatch(
        setAttachingNewPermit({
          started: false,
          taskId: "",
          form: {},
          county: "",
          city: "",
          addedPermits: [],
          additionalPermits: {},
          usedContractors: [],
          packageSnapshot: null,
          prevPermitGrid: {
            page: 0,
            pageSize: 5,
            rowCount: 0,
          },
        })
      );
    }

    dispatch(
      setQuickActionAddress({
        fullStreetAddress: "",
        houseNumber: "",
        streetName: "",
        county: "",
        city: "",
        parcelID: "",
        coNum: "",
      })
    );

    // Clear navigation state completely
    if (fromPermit?.fromPermit || attachingNewPermit?.started) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [
    isOpen,
    navigate,
    location.pathname,
    fromPermit?.fromPermit,
    setStartPermitOpen,
    setDisplayImage,
    attachingNewPermit?.started,
    dispatch,
  ]);

  const handleStartPermitPackage = useCallback(async () => {
    try {
      setStartPermitLoading(true);

      const updatedSelectedForms = selectedForms.map((form) =>
        form === "stormwater" ? stormwaterSelection : form
      );

      const isAdditional =
        attachingNewPermit?.started && attachingNewPermit?.taskId;
      const url = isAdditional
        ? `${process.env.REACT_APP_BASE_URL}/nocApi/building/additional-permit/create`
        : `${process.env.REACT_APP_BASE_URL}/nocApi/building/start-create-permit-package`;

      const payload = isAdditional
        ? {
            task_id: attachingNewPermit.taskId,
            county: props?.county,
            city: selectedCity,
            selectedForms: updatedSelectedForms,
            contractor: contractors?.[contractorIndex],
            currentSession: currentSession,
            ppName: ppName,
            connectToCity: connectToCity,
            selectedCityUser: selectedCityUser,
            selectedPermitType: selectedPermitType,
            citySetupData: attachingNewPermit?.additionalPermitCitySetup || {},
            formContractors: formContractors, // Contractor per form (for Generator Permit)
            autoEmailEnabled: autoEmailEnabled, // Email configuration
            cityEmail: cityEmail,
            responseEmail: responseEmail,
            emailRows: emailRows, // New email rows structure
          }
        : {
            county: props?.county,
            city: selectedCity,
            selectedForms: updatedSelectedForms,
            selectedCountyForms: selectedCountyForms,
            contractor: contractors?.[contractorIndex],
            currentSession: currentSession,
            ppName: ppName,
            privateProviderSignerType: privateProviderSignerType,
            connectToCity: connectToCity,
            selectedCityUser: selectedCityUser,
            selectedPermitType: selectedPermitType,
            formContractors: formContractors, // Contractor per form (for Generator Permit)
            autoEmailEnabled: autoEmailEnabled, // Email configuration
            cityEmail: cityEmail,
            responseEmail: responseEmail,
            emailRows: emailRows, // New email rows structure
          };

      // Validate email rows if auto-email is enabled
      // Only check that at least one cc and at least one bcc is filled
      if (autoEmailEnabled && emailRows) {
        const hasAtLeastOneCc = emailRows.some(
          (row) => row.cc && row.cc.trim() !== ""
        );
        const hasAtLeastOneBcc = emailRows.some(
          (row) => row.bcc && row.bcc.trim() !== ""
        );

        if (!hasAtLeastOneCc || !hasAtLeastOneBcc) {
          setEmailRowOnError([
            ...(!hasAtLeastOneCc ? [{ rowIndex: 0, field: "cc" }] : []),
            ...(!hasAtLeastOneBcc ? [{ rowIndex: 0, field: "bcc" }] : []),
          ]);
          setStartPermitLoading(false);
          return;
        }
      }

      // Clear errors if validation passes
      setEmailRowOnError([]);

      const resp = await axios.post(url, payload, { withCredentials: true });

      if (resp?.data?.success) {
        await dispatch(setNewTempPermitData(resp?.data?.data));
        navigate("/permits/my-permits");
        // For additional permits, don't navigate away - just close the modal
        // if (isAdditional) {
        //   handleCloseStartPermit();
        // } else {
        //   navigate("/permits/my-permits");
        // }
        // setTimeout(() => {
        //   handleCloseStartPermit();
        // }, 500);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        await dispatch(disconnectWebsocket(currentSession));
        sessionStorage.removeItem("__nocCreator__r_rid");
        sessionStorage.removeItem("exp");
        setTimeout(() => {
          dispatch(setExpiredSession(true));
          navigate("/login");
        }, 100);
        navigate("/login");
      }
    } finally {
      setStartPermitLoading(false);
    }
  }, [
    props?.county,
    selectedCity,
    selectedForms,
    selectedCountyForms,
    contractorIndex,
    currentSession,
    ppName,
    privateProviderSignerType,
    dispatch,
    navigate,
    stormwaterSelection,
    connectToCity,
    selectedCityUser,
    selectedPermitType,
    contractors,
    attachingNewPermit,
    formContractors,
    autoEmailEnabled,
    cityEmail,
    responseEmail,
    emailRows,
  ]);

  useEffect(() => {
    if (isOpen) {
      const num = currentSession === "userSession" ? 30 : 20;
      // Don't auto-trigger permit creation when in additional permit mode
      if (currentStep === num && page === 1 && !attachingNewPermit?.started) {
        handleStartPermitPackage();
      }
    }
  }, [
    currentStep,
    currentSession,
    handleStartPermitPackage,
    isOpen,
    page,
    attachingNewPermit?.started,
  ]);

  useEffect(() => {
    const newImages = JSON.stringify(props?.displayImage || []);
    const prevImages = prevImagesRef.current;

    if (leftGridRef.current && newImages !== prevImages) {
      leftGridRef.current.scrollTo(0, 0);
      prevImagesRef.current = newImages;
    }
  }, [props?.displayImage]);

  useEffect(() => {
    if (!props?.fromPermit || Object.keys(props.fromPermit).length === 0) {
      if (selectedCity !== "" && !quickAddressRef?.current) {
        setAvailableForms([]);
        setSelectedCity("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.countyImg, props?.startPermitOpen, props?.fromPermit]);

  useEffect(() => {
    const loadPermitForms = async () => {
      if (isFetchingRef.current) return;

      isFetchingRef.current = true;
      try {
        setCountyLoading(true);
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/data/get-county-permit`,
          { currentSession: currentSession, county: props?.county },
          { withCredentials: true }
        );
        await dispatch(
          addAvailablePermitForms(
            resp?.data?.forms,
            resp?.data?.permit_type_forms
          )
        );
        props?.setReadyLoad(false);
      } catch (e) {
        if (e.response?.status === 401) {
          await dispatch(disconnectWebsocket(currentSession));
          sessionStorage.removeItem("__nocCreator__r_rid");
          sessionStorage.removeItem("exp");
          setTimeout(() => {
            dispatch(setExpiredSession(true));
            navigate("/login");
          }, 100);
          navigate("/login");
        }
      } finally {
        isFetchingRef.current = false;
        setCountyLoading(false);
      }
    };

    if (props?.readyLoad) {
      loadPermitForms();
    }
  }, [props?.readyLoad, currentSession, dispatch, navigate, props]);

  return (
    <Drawer
      slotProps={{
        paper: {
          sx: {
            width: "100vw",
          },
        },
      }}
      anchor="right"
      open={props?.startPermitOpen}
      onClose={handleCloseStartPermit}
    >
      <Grid
        container
        sx={{
          display: "flex",
          overflow: "hidden",
          width: "100%",
        }}
      >
        {/* LEFT SIDE */}
        <Grid
          size={{ md: !up1177 ? 6 : 6 }}
          ref={leftGridRef}
          sx={{
            overflow: "auto",
            position: "relative",
            mb: 4,
          }}
        >
          <Box
            sx={{
              mx: "auto",
              width: "100%",
              maxWidth: "1000px",
              height: "100vh",
            }}
          >
            {page === 0 ? (
              <Box sx={{ position: "relative", width: "100%" }}>
                {attachingNewPermit?.started && (
                  <Box
                    sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      mx: "auto",
                      mt: 1,
                      mb: 1,
                      px: 2,
                      py: 1.5,
                      maxWidth: "1000px",
                      borderRadius: 3,
                      color: "#133055",
                      background:
                        "linear-gradient(135deg, #f7fbff 0%, #ecf3ff 100%)",
                      boxShadow:
                        "0 10px 20px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.6)",
                      border: "1px solid #d9e6f4",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0,
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{ mb: 0.5 }}
                      >
                        Additional permits will be attached to this property’s
                        work
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.85, mb: 2 }}>
                        Review your existing NOC below and select additional
                        forms on the right.
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          justifyContent: "center",
                        }}
                      >
                        <InfoChip
                          label="Property"
                          value={(function () {
                            const pkg =
                              attachingNewPermit?.packageSnapshot || {};
                            const projectAddress = pkg?.project_address;
                            return projectAddress || "―";
                          })()}
                        />
                        <InfoChip
                          label="Owner"
                          value={(function () {
                            const pkg =
                              attachingNewPermit?.packageSnapshot || {};
                            const o = pkg?.property_owner || {};
                            const name = o?.name;

                            return name || "―";
                          })()}
                        />
                        <InfoChip
                          label="Contractor"
                          value={(function () {
                            const pkg =
                              attachingNewPermit?.packageSnapshot || {};
                            return (
                              pkg?.contractor?.businessName ||
                              pkg?.contractor?.name ||
                              "―"
                            );
                          })()}
                        />
                        <InfoChip
                          label="Work Type"
                          value={(function () {
                            const pkg =
                              attachingNewPermit?.packageSnapshot || {};
                            return pkg?.documents?.noc?.fields?.general || "―";
                          })()}
                        />
                      </Box>
                    </Box>
                  </Box>
                )}

                {(() => {
                  // For additional permit mode, show simplified NOC display
                  if (attachingNewPermit?.started) {
                    const pkg = attachingNewPermit?.packageSnapshot;
                    const nocDoc = pkg?.documents?.noc;
                    const nocImages = nocDoc?.signed || [];
                    // );

                    // If displayImage is set (from form card click), show that instead
                    if (
                      props?.displayImage &&
                      props?.displayImage?.length > 0
                    ) {
                      return props?.displayImage?.map((item, index) => (
                        <Magnifier
                          key={index}
                          src={item}
                          lensSize={400}
                          zoomFactor={4}
                          handleImageError={() => handleImageError(index)}
                          signedLoading={signedLoading}
                        />
                      ));
                    }

                    // Check if NOC has valid signed URLs
                    const hasValidNocImages =
                      nocImages.length > 0 &&
                      nocImages.some((url) => !isSignedUrlExpired(url));

                    if (hasValidNocImages) {
                      // Show NOC images if they exist and are not expired
                      return nocImages
                        .filter((url) => !isSignedUrlExpired(url))
                        .map((item, index) => (
                          <Magnifier
                            key={index}
                            src={item}
                            lensSize={400}
                            zoomFactor={4}
                            handleImageError={() => handleImageError(index)}
                            signedLoading={signedLoading}
                          />
                        ));
                    } else {
                      // Show instruction text if no valid NOC images
                      return (
                        <Box
                          sx={{
                            display: "flex",

                            height: "calc(100vh - 250px)", // Subtract space for the banner card
                            justifyContent: "center",
                            alignItems: "center",
                            flexDirection: "column",
                            textAlign: "center",
                            px: 4,
                          }}
                        >
                          <Typography
                            variant="h4"
                            color="primary"
                            fontWeight="bold"
                            sx={{ mb: 2 }}
                          >
                            Start by selecting forms from the right side
                          </Typography>
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ maxWidth: "600px" }}
                          >
                            Choose the additional permit forms you want to add
                            to this property's work. The Notice of Commencement
                            is already created and will be included
                            automatically.
                          </Typography>
                        </Box>
                      );
                    }
                  }

                  // For normal mode, show all display images
                  if (props?.displayImage && props?.displayImage?.length > 0) {
                    return props?.displayImage?.map((item, index) => (
                      <Magnifier
                        key={index}
                        src={item}
                        lensSize={400}
                        zoomFactor={4}
                      />
                    ));
                  }

                  return null;
                })() || (
                  <Box
                    sx={{
                      display: "flex",
                      width: "100%",
                      height: "100vh",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                    }}
                  >
                    <CircularProgress color="warning" size="5rem" />
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  mt: 10,
                  // display: "flex",
                  mx: "auto",
                  height: "100vh",
                  // justifyContent: "center",
                  alignItems: "center",
                  // flexDirection: "column",
                }}
              >
                <Typography
                  mt={1}
                  variant="h3"
                  color="primary"
                  fontWeight="bold"
                  align="center"
                >
                  Forms to be Created
                </Typography>

                <Box
                  sx={{
                    overflow: "auto",
                    width: "100%",
                    pb: "80px",
                  }}
                >
                  <Box
                    sx={{
                      mt: 5,
                      width: "100%", // Adjust the max height as needed
                      px: 2, // Add padding on left and right
                      boxSizing: "border-box",
                    }}
                  >
                    {availableForms?.filter((form) =>
                      selectedForms?.includes(form.name)
                    )?.length > 0 ? (
                      <Grid
                        container
                        spacing={2}
                        sx={{ height: "100%" }}
                        mt={2}
                        mb={2}
                      >
                        {availableForms
                          ?.filter((form) => selectedForms?.includes(form.name))
                          ?.map((item, index) => (
                            <Grid
                              size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                              key={index}
                            >
                              <Card
                                sx={{
                                  border: "3px solid #d3d3d3",
                                  boxShadow:
                                    "6px 12px 16px 3px rgba(0, 0, 0, 0.5)",
                                  p: 1,
                                  minHeight: "14rem",
                                  // backgroundColor: "rgba(56, 142, 60, 0.3)",
                                }}
                              >
                                <CardMedia
                                  component="img"
                                  height="140"
                                  image={`${process.env.REACT_APP_URL}${item?.cover_img}`}
                                  alt={item.label}
                                />
                                <CardContent
                                  className="bottomCard"
                                  sx={{ px: 0, pt: 1 }}
                                >
                                  <Typography
                                    align="center"
                                    variant="body2"
                                    color="primary"
                                    fontWeight="bold"
                                    component="div"
                                  >
                                    {item.name === "stormwater" &&
                                    stormwaterSelection
                                      ? item?.forms?.find(
                                          (storm) =>
                                            storm?.name === stormwaterSelection
                                        )?.label
                                      : item.label}
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                      </Grid>
                    ) : (
                      selectedForms?.length === 0 && (
                        <Typography
                          align="center"
                          my={3}
                          variant="h6"
                          sx={{ color: "red" }}
                        >
                          There are no documents selected
                        </Typography>
                      )
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>

        {/* RIGHT SIDE */}
        <Grid
          size={{ md: !up1177 ? 6 : 6 }}
          sx={{
            position: "relative",
          }}
        >
          <Box
            className={
              currentSession === "userSession"
                ? currentStep === 10
                  ? "step11"
                  : currentStep === 11
                    ? "step12"
                    : ""
                : currentSession === "subUserSession"
                  ? currentStep === 8
                    ? "step11"
                    : currentStep === 9
                      ? "step12"
                      : ""
                  : ""
            }
            sx={{
              position: "relative",
              mx: "auto",
              overflow: "hidden",
              borderBottom:
                contractors?.length === 0 ? undefined : "4px solid #d3d3d3",
              maxWidth: "800px",
            }}
          >
            <IconButton
              disabled={startPermitLoading}
              onClick={handleCloseStartPermit}
              sx={{ position: "absolute", right: 10, top: 10 }}
            >
              <CloseIcon />
            </IconButton>
            <SelectPermitPackageForms
              availableForms={availableForms}
              setAvailableForms={setAvailableForms}
              county={props?.county}
              selectedForms={selectedForms}
              setSelectedForms={setSelectedForms}
              selectedCountyForms={selectedCountyForms}
              setSelectedCountyForms={setSelectedCountyForms}
              displayImage={props?.displayImage}
              setDisplayImage={props?.setDisplayImage}
              selectedCity={selectedCity}
              fromPermit={props?.fromPermit?.fromPermit}
              setSelectedCity={setSelectedCity}
              availableCitiesNum={props?.availableCitiesNum}
              page={page}
              contractorIndex={contractorIndex}
              setContractorIndex={setContractorIndex}
              cities={cities}
              setCities={setCities}
              countyLoading={countyLoading}
              ppName={ppName}
              setPpName={setPpName}
              stormwaterSelection={stormwaterSelection}
              setStormwaterSelection={setStormwaterSelection}
              privateProviderSignerType={privateProviderSignerType}
              setPrivateProviderSignerType={setPrivateProviderSignerType}
              selectedPermitType={selectedPermitType}
              setSelectedPermitType={setSelectedPermitType}
              groupedForms={groupedForms}
              setGroupedForms={setGroupedForms}
              connectToCity={connectToCity}
              setConnectToCity={setConnectToCity}
              selectedCityUser={selectedCityUser}
              setSelectedCityUser={setSelectedCityUser}
              addNewUser={addNewUser}
              setAddNewUser={setAddNewUser}
              contractors={contractors}
              formContractors={formContractors}
              setFormContractors={setFormContractors}
              isPP={isPP}
              autoEmailEnabled={autoEmailEnabled}
              setAutoEmailEnabled={setAutoEmailEnabled}
              cityEmail={cityEmail}
              setCityEmail={setCityEmail}
              responseEmail={responseEmail}
              setResponseEmail={setResponseEmail}
              emailRows={emailRows}
              setEmailRows={setEmailRows}
              userEmail={currentUser?.user?.email || currentUser?.email || ""}
              emailRowOnError={emailRowOnError}
              setEmailRowOnError={setEmailRowOnError}
            />
          </Box>
          {!isOpen && (
            <Toolbar
              sx={{
                px: 0,
                pb: 1,
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                opacity: 1,
              }}
            >
              <Grid container sx={{ width: "100%" }}>
                <Grid size={{ xs: 6 }}>
                  <></>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Box
                    sx={{ display: "flex", gap: 2, justifyContent: "center" }}
                  >
                    <Button
                      size="large"
                      onClick={() => {
                        if (isOpen) {
                          return;
                        }
                        setPage(page - 1);
                      }}
                      disabled={page === 0 || startPermitLoading}
                      sx={{ minWidth: "125px" }}
                      variant="contained"
                      startIcon={<ArrowBackIcon />}
                    >
                      Back
                    </Button>
                    <LoadingButton
                      loading={startPermitLoading}
                      size="large"
                      color={
                        (props?.county === "Duval" &&
                          selectedCity.toLowerCase() === "jacksonville" &&
                          page === 2 &&
                          contractors?.length > 0) ||
                        ((props?.county !== "Duval" ||
                          selectedCity.toLowerCase() !== "jacksonville") &&
                          page === 1 &&
                          contractors?.length > 0)
                          ? "success"
                          : "primary"
                      }
                      disabled={
                        (contractors?.length === 0 &&
                          page === 1 &&
                          // Skip contractor requirement for Generator Permit in St. Johns
                          !(
                            selectedPermitType === "generator_permit" &&
                            (props?.county?.toLowerCase() === "st_johns" ||
                              props?.county?.toLowerCase() === "st johns" ||
                              props?.county?.toLowerCase() === "st. johns")
                          )) ||
                        selectedPermitType === "" ||
                        (page === 2 && connectToCity && !selectedCityUser) ||
                        (page === 2 && connectToCity && addNewUser === true) ||
                        (selectedForms.length === 0 && page === 1) ||
                        !areRequiredContractorsSelected() ||
                        !isEmailConfigurationValid()
                      }
                      onClick={() => {
                        if (
                          (page === 1 &&
                            contractors?.length > 0 &&
                            (props?.county !== "Duval" ||
                              selectedCity.toLowerCase() !== "jacksonville")) ||
                          (props?.county === "Duval" &&
                            selectedCity.toLowerCase() === "jacksonville" &&
                            page === 2)
                        ) {
                          handleStartPermitPackage();
                        } else {
                          setPage(page + 1);
                        }

                        if (isOpen && page === 0) {
                          setTimeout(() => {
                            setCurrentStep((s) =>
                              s === tourSteps?.length - 1 ? 0 : s + 1
                            );
                          }, 200);
                        }
                      }}
                      sx={{ minWidth: "125px" }}
                      variant="contained"
                      endIcon={
                        (props?.county === "Duval" &&
                          selectedCity.toLowerCase() === "jacksonville" &&
                          page === 2 &&
                          contractors?.length > 0) ||
                        ((props?.county !== "Duval" ||
                          selectedCity.toLowerCase() !== "jacksonville") &&
                          page === 1 &&
                          contractors?.length > 0) ? undefined : (
                          <ArrowForwardIcon />
                        )
                      }
                    >
                      {(props?.county === "Duval" &&
                        selectedCity.toLowerCase() === "jacksonville" &&
                        page === 2 &&
                        contractors?.length > 0) ||
                      ((props?.county !== "Duval" ||
                        selectedCity.toLowerCase() !== "jacksonville") &&
                        page === 1 &&
                        contractors?.length > 0)
                        ? "Start Creating Permit Package"
                        : "Continue"}
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Toolbar>
          )}
        </Grid>
      </Grid>
    </Drawer>
  );
};

export default StartPermit;
